import { useEffect, useRef, useState } from "react";
import { animate, motion, useInView } from "framer-motion";
import { Reveal } from "@/components/site/Motion";
import { STATS } from "@/data/site";
import { T, useContent } from "@/context/ContentContext";
import { usePartners, partnerHeight } from "@/hooks/usePartners";
import { assetUrl } from "@/lib/api";

function ScrollCounter({ value, suffix, testId }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const [n, setN] = useState(0);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!inView) return undefined;
    const controls = animate(0, value, {
      duration: 2, ease: [0.22, 1, 0.36, 1], onUpdate: (v) => setN(Math.round(v)),
      onComplete: () => setReady(true),
    });
    return () => controls.stop();
  }, [inView, value]);
  useEffect(() => {
    if (!ready) return undefined;
    let last = Date.now();
    const tick = () => { last = Date.now(); setN((v) => v + 1); };
    const onScroll = () => { if (Date.now() - last >= 400) tick(); };
    const idle = setInterval(() => { if (Date.now() - last >= 3000) tick(); }, 500);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); clearInterval(idle); };
  }, [ready]);
  return (
    <span ref={ref} data-testid={testId} className="inline-flex overflow-hidden font-display font-black tracking-tight text-blush text-4xl sm:text-5xl lg:text-6xl tabular-nums">
      {ready
        ? <motion.span key={n} initial={{ y: "60%", opacity: 0.3 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}>{n.toLocaleString("en-IN")}</motion.span>
        : <span>{n.toLocaleString("en-IN")}</span>}
      <span>{suffix}</span>
    </span>
  );
}

function CountUp({ value, suffix, testId }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return undefined;
    const controls = animate(0, value, { duration: 2, ease: [0.22, 1, 0.36, 1], onUpdate: (v) => setN(Math.round(v)) });
    return () => controls.stop();
  }, [inView, value]);
  return (
    <span ref={ref} data-testid={testId} className="font-display font-black tracking-tight text-blush text-4xl sm:text-5xl lg:text-6xl tabular-nums">
      {n.toLocaleString("en-IN")}{suffix}
    </span>
  );
}

function PartnerMark({ p }) {
  return (
    <div className="flex items-center justify-center h-20 sm:h-24 w-52 sm:w-64 shrink-0 rounded-2xl bg-white px-6 transition-transform duration-500 hover:-translate-y-1" data-testid={`partner-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      {p.logo
        ? <img src={assetUrl(p.logo)} alt={`${p.name} logo`} loading="lazy" className={`${partnerHeight(p.size)} w-auto max-w-full object-contain`} />
        : <span className="font-display text-xl sm:text-3xl font-extrabold tracking-tight text-ink">{p.name}</span>}
    </div>
  );
}

function StatCell({ s, i }) {
  const { texts, editing } = useContent();
  const k = `stats.${i}`;
  const value = Number(texts[`${k}.value`]) || s.value;
  const suffix = texts[`${k}.suffix`] ?? s.suffix;
  const cls = "font-display font-black tracking-tight text-blush text-4xl sm:text-5xl lg:text-6xl tabular-nums";
  return (
    <div className={`bg-ink px-4 py-10 sm:py-12 text-center ${i === STATS.length - 1 ? "col-span-2 lg:col-span-1" : ""}`}>
      {editing
        ? <span className={cls} data-testid={`stat-${i}`}><T k={`${k}.value`}>{String(value)}</T><T k={`${k}.suffix`}>{suffix}</T></span>
        : s.repeat
          ? <ScrollCounter value={value} suffix={suffix} testId={`stat-${i}`} />
          : <CountUp value={value} suffix={suffix} testId={`stat-${i}`} />}
      <T k={`${k}.label`} as="p" className="mt-3 text-sm sm:text-base text-cream/85">{s.label}</T>
    </div>
  );
}

export function StatsPartners() {
  const partners = usePartners();
  return (
    <section className="relative bg-ink text-cream overflow-hidden grain py-20 sm:py-28" data-testid="stats-partners-section">
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal><T k="stats.eyebrow" as="p" className="eyebrow text-blush mb-10 text-center">Trippy Tastic in numbers</T></Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-px rounded-[28px] overflow-hidden border border-blush/30 bg-blush/20" data-testid="stats-grid">
          {STATS.map((s, i) => <StatCell key={i} s={s} i={i} />)}
        </div>
        {partners.length > 0 && <div className="mt-20" data-testid="partners">
          <Reveal className="text-center mb-10">
            <T k="partners.eyebrow" as="p" className="eyebrow text-gold">Trusted by</T>
            <h2 className="mt-3 font-display text-3xl sm:text-5xl font-extrabold tracking-tight"><T k="partners.title.0">Our valuable</T> <T k="partners.title.1" className="font-serif italic font-normal text-blush">partners.</T></h2>
          </Reveal>
          <div className="overflow-hidden [mask-image:linear-gradient(90deg,transparent,#000_8%,#000_92%,transparent)]" data-testid="partners-marquee">
            <div className="partners-track flex w-max gap-4 sm:gap-5 py-2">
              {Array.from({ length: Math.max(2, Math.ceil(8 / partners.length)) * 2 }).flatMap((_, k) =>
                partners.map((p) => <PartnerMark key={`${k}-${p.id || p.name}`} p={p} />))}
            </div>
          </div>
        </div>}
      </div>
    </section>
  );
}
